import { ListboxButton } from '@headlessui/react'
import classnames from 'classnames'
import { useCallback } from 'react'
import Spinner from '../../Loaders/Spinner'
import { useUIComponentsContext } from '../../Provider/UiComponentsProvider'
import TruncatedTooltipText from '../../Tooltip/TruncatedTooltipText'
import type { DropdownButtonRenderProps } from '../Dropdown'

const theme = {
  button: {
    shared:
      'relative w-100 rounded-md border data-[open]:border-electric-violet-500',
    default: 'border-oxford-gray-200',
    error: 'border-watermelon-800',
    disabled: 'bg-oxford-gray-50 border-[#E5E7EB]'
  },
  buttonLabel: {
    shared: 'block truncate text-sm leading-[1.9rem]',
    default: 'text-oxford-gray-800',
    error: 'text-watermelon-900',
    placeholder: 'text-oxford-gray-300',
    disabled: 'text-oxford-gray-400'
  }
}

export const DropdownButtonIcon = ({
  loading,
  value,
  disabled,
  renderClearButton,
  onClear
}: Partial<DropdownButtonRenderProps> & { onClear: () => void }) => {
  if (typeof renderClearButton === 'function' && !!value && !disabled) {
    return renderClearButton(onClear)
  }

  return (
    <span className="absolute inset-y-0 right-1 px-2 flex items-center pointer-events-none">
      {loading ? (
        <Spinner />
      ) : (
        <i
          className={classnames([
            'fas fa-caret-down text-sm',
            {
              'text-oxford-gray-800': !disabled,
              'text-oxford-gray-200': disabled
            }
          ])}
          aria-hidden="true"
        />
      )}
    </span>
  )
}

const DropdownButton = ({
  id = '',
  name = '',
  className,
  value,
  placeholder,
  renderItem,
  textField = 'name',
  paddingClass = 'py-2 pl-3 pr-5',
  disabled,
  fillAnimationClassName,
  open,
  loading,
  onFillAnimationEnd,
  popperPlacement,
  error,
  onChange,
  onBlur,
  renderClearButton,
  renderButtonContent,
  setButtonElement,
  bgColorClass
}: DropdownButtonRenderProps) => {
  const { t } = useUIComponentsContext()
  const onClear = useCallback(() => onChange(null), [onChange])

  if (!placeholder && typeof t === 'function') {
    placeholder = t('expressions.select')
  }

  return (
    <ListboxButton
      ref={(newRef) => {
        if (newRef) {
          setButtonElement((ref) => {
            if (ref) {
              return ref
            }

            return newRef
          })
        }
      }}
      onAnimationEnd={onFillAnimationEnd}
      onBlur={onBlur}
      className={classnames([
        theme.button.shared,
        fillAnimationClassName,
        bgColorClass,
        {
          [theme.button.default]: !error && !disabled,
          [theme.button.error]: !!error && !disabled,
          [theme.button.disabled]: disabled,
          'data-[open]:rounded-b-none':
            popperPlacement && popperPlacement !== 'top',
          'data-[open]:rounded-t-none':
            popperPlacement && popperPlacement === 'top'
        },
        className
      ])}
      name={name}
      id={id}
    >
      {value && typeof renderItem === 'function' ? (
        renderItem({ item: value, inButton: true })
      ) : typeof renderButtonContent === 'function' ? (
        renderButtonContent({
          item: value,
          placeholder
        })
      ) : (
        <span className={classnames('flex w-full items-center', paddingClass)}>
          <TruncatedTooltipText
            text={value?.[textField] ?? placeholder}
            className={classnames([
              theme.buttonLabel.shared,
              {
                [theme.buttonLabel.default]: !error && !disabled && value,
                [theme.buttonLabel.error]: !!error && !open,
                [theme.buttonLabel.disabled]: disabled,
                [theme.buttonLabel.placeholder]:
                  !!placeholder && !error && !disabled && !value
              }
            ])}
          />
        </span>
      )}

      <DropdownButtonIcon
        loading={loading}
        value={value}
        disabled={disabled}
        onClear={onClear}
        renderClearButton={renderClearButton}
      />
    </ListboxButton>
  )
}

export { theme as dropdownButtonTheme }

export default DropdownButton
